import { del, download, get, upload } from '../../shared/apiClient';
import { buildUrl, ENDPOINTS } from '../../shared/endpoints';
import type { RequestConfig } from '../../shared/httpTypes';

export interface UserSummary {
  user_uuid: string;
  full_name: string;
  email: string;
  nif: string;
  country: string | null;
  created_at: string;
  updated_at: string;
}

export interface FlowSessionSummary {
  session_uuid: string;
  country: string | null;
  tax_year: number;
  language: string;
  status: string;
  current_screen_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  source: string | null;
}

export interface QuestionMeta {
  question_text: string;
  screen_id: string;
  screen_title: string;
  field_type: string;
  category: string;
  screen_order: number;
}

export interface FlowSessionDetail extends FlowSessionSummary {
  user: UserSummary | null;
  answers: Record<string, unknown>;
  visited_screens: string[];
  screen_history: string[];
  last_tax_calculation: Record<string, unknown> | null;
  aeat_snapshot_uuids: string[];
  question_metadata: Record<string, QuestionMeta>;
}

export interface UserDetail extends UserSummary {
  flow_sessions: FlowSessionSummary[];
}

export interface UserListResponse {
  items: UserSummary[];
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AEATFiscalSnapshot {
  snapshot_uuid: string;
  flow_session_uuid: string | null;
  user_uuid: string | null;
  aeat_integration_session_id: string | null;
  tax_year: number | null;
  nif: string | null;
  fetched_at: string;
  parsed_fiscal_data: Record<string, unknown>;
}

function adminHeaders(token: string): RequestConfig {
  return { headers: { 'X-Admin-Token': token } };
}

export async function fetchUsers(
  token: string,
  params?: {
    q?: string;
    country?: string;
    tax_year?: number;
    status?: string;
    source?: string;
    limit?: number;
    offset?: number;
  },
): Promise<UserListResponse> {
  const url = buildUrl(ENDPOINTS.adminReview.users, { query: params as Record<string, string | number> });
  const { data } = await get<UserListResponse>(url, undefined, adminHeaders(token));
  return data;
}

export async function fetchUserDetail(token: string, userUuid: string): Promise<UserDetail> {
  const url = buildUrl(ENDPOINTS.adminReview.user, { params: { userUuid } });
  const { data } = await get<UserDetail>(url, undefined, adminHeaders(token));
  return data;
}

export async function fetchFlowSessionDetail(
  token: string,
  sessionUuid: string,
): Promise<FlowSessionDetail> {
  const url = buildUrl(ENDPOINTS.adminReview.flowSession, { params: { sessionUuid } });
  const { data } = await get<FlowSessionDetail>(url, undefined, adminHeaders(token));
  return data;
}

export async function fetchFlowSessionAeat(
  token: string,
  sessionUuid: string,
): Promise<AEATFiscalSnapshot[]> {
  const url = buildUrl(ENDPOINTS.adminReview.flowSessionAeat, { params: { sessionUuid } });
  const { data } = await get<AEATFiscalSnapshot[]>(url, undefined, adminHeaders(token));
  return data;
}

export async function fetchAeatSnapshot(
  token: string,
  snapshotUuid: string,
): Promise<AEATFiscalSnapshot> {
  const url = buildUrl(ENDPOINTS.adminReview.aeatSnapshot, { params: { snapshotUuid } });
  const { data } = await get<AEATFiscalSnapshot>(url, undefined, adminHeaders(token));
  return data;
}

// ---------------------------------------------------------------------------
// Document management
// ---------------------------------------------------------------------------

export interface UserDocumentResponse {
  id: string;
  filename: string;
  label: string | null;
  content_type: string;
  size_bytes: number;
  created_at: string;
  flow_session_uuid: string | null;
}

export async function uploadUserDocument(
  userId: string,
  file: File,
  label: string,
  token: string,
  sessionUuid?: string,
): Promise<UserDocumentResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (label) formData.append('label', label);
  if (sessionUuid) formData.append('flow_session_uuid', sessionUuid);
  const url = ENDPOINTS.adminDocuments.upload(userId);
  const { data } = await upload<UserDocumentResponse>(url, formData, adminHeaders(token));
  return data;
}

export async function listUserDocuments(
  userId: string,
  token: string,
): Promise<UserDocumentResponse[]> {
  const url = ENDPOINTS.adminDocuments.list(userId);
  const { data } = await get<UserDocumentResponse[]>(url, undefined, adminHeaders(token));
  return data;
}

export async function downloadUserDocument(
  docId: string,
  token: string,
): Promise<Blob> {
  const url = ENDPOINTS.adminDocuments.download(docId);
  return download(url, undefined, adminHeaders(token));
}

export async function deleteUserDocument(
  docId: string,
  token: string,
): Promise<void> {
  const url = ENDPOINTS.adminDocuments.delete(docId);
  await del(url, adminHeaders(token));
}
